import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { from, of } from 'rxjs';

import { UserRole } from '../../../../core/models/enums';
import { ConteoMedioBorrador, RecepcionTurnoDialogData, ResultadoControlTurno, VendedoraTurno } from '../../../../core/models/cash-shift.model';
import { AuthService } from '../../../../core/services/auth.service';
import { BranchService } from '../../../../core/services/branch.service';
import { CashShiftService } from '../../../../core/services/cash-shift.service';
import { StaffService } from '../../../../core/services/staff.service';
import { UiService } from '../../../../core/services/ui.service';
import { cantidadDiferencias, filasResultadoControl } from '../../../../core/utils/cash-shift.util';
import { ModalBoxComponent } from '../../../../shared/components/modal-box/modal-box.component';

/**
 * Recepción de caja en un cambio de turno. Quien llega cuenta a ciegas (no ve lo que dice el sistema ni lo que
 * contó quien entregó) y recién al confirmar el sistema muestra qué coincide y dónde hay diferencias.
 */
@Component({
  selector: 'app-shift-reception-dialog',
  imports: [
    CurrencyPipe,
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    ModalBoxComponent,
    NgClass
  ],
  templateUrl: './shift-reception-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShiftReceptionDialogComponent {
  // 1. Inyecciones
  private readonly dialogRef = inject<MatDialogRef<ShiftReceptionDialogComponent, boolean>>(MatDialogRef);
  protected readonly data = inject<RecepcionTurnoDialogData>(MAT_DIALOG_DATA);
  private readonly authService = inject(AuthService);
  private readonly branchService = inject(BranchService);
  private readonly cashShiftService = inject(CashShiftService);
  private readonly staffService = inject(StaffService);
  private readonly uiService = inject(UiService);

  // 2. Estado y recursos
  readonly contado = signal<number | null>(null);
  readonly conteos = signal<ConteoMedioBorrador[]>(
    this.data.pendiente.methods.map(m => ({ key: m.method_key, name: m.method_name, type: m.method_type, monto: null }))
  );
  readonly notas = signal<string>('');
  readonly vendedoraId = signal<string | null>(null);
  readonly guardando = signal<boolean>(false);
  /** Resultado del control: mientras es null el usuario está contando a ciegas. */
  readonly resultado = signal<ResultadoControlTurno | null>(null);

  /** Con el login compartido de sucursal (punto de venta) hay que elegir quién recibe la caja. */
  readonly pideVendedora = computed(() => this.authService.userRole() === UserRole.POS);

  readonly vendedorasResource = rxResource<VendedoraTurno[], { businessId: string | null; branchId: string | null; pide: boolean }>({
    params: () => ({
      businessId: this.authService.userProfile()?.business_id ?? null,
      branchId: this.branchService.workingBranchId(),
      pide: this.pideVendedora()
    }),
    stream: ({ params }) => {
      if (!params.pide) return of([]);
      return from(
        this.staffService.getResources(params.businessId, params.branchId).then(res =>
          res.filter(r => r.is_active !== false && r.role !== UserRole.POS).map(r => ({ id: r.id, name: r.name }))
        )
      );
    }
  });

  /** Quien entregó no puede recibir: se saca de la lista. */
  readonly vendedorasDisponibles = computed(() =>
    (this.vendedorasResource.value() ?? []).filter(v => v.id !== this.data.pendiente.handed_by_seller_id));

  readonly vendedoraNombre = computed(() =>
    this.vendedorasDisponibles().find(v => v.id === this.vendedoraId())?.name ?? '');

  readonly filas = computed(() => {
    const r = this.resultado();
    return r ? filasResultadoControl(r) : [];
  });
  readonly diferencias = computed(() => cantidadDiferencias(this.filas()));

  readonly puedeControlar = computed(() => {
    const contado = this.contado();
    if (contado === null || contado < 0) return false;
    if (this.pideVendedora() && !this.vendedoraId()) return false;
    if (this.conteos().some(c => c.monto === null || c.monto < 0)) return false;
    return !this.guardando();
  });

  // 5. Métodos
  /** Cambia el monto contado de un medio de pago. */
  cambiarMonto(indice: number, monto: number | null): void {
    this.conteos.update(lista => lista.map((c, i) => i === indice ? { ...c, monto } : c));
  }

  /** Envía el conteo a ciegas, libera la caja y muestra el resultado del control. */
  async controlar(): Promise<void> {
    if (!this.puedeControlar()) return;
    this.guardando.set(true);
    try {
      const { resultado, error } = await this.cashShiftService.validar(
        this.data.pendiente.id,
        this.pideVendedora() ? this.vendedoraId() : null,
        this.contado() ?? 0,
        this.conteos().map(c => ({ method_key: c.key, verified_amount: c.monto ?? 0 })),
        this.notas().trim()
      );
      if (error || !resultado) {
        this.uiService.showError(error?.message || 'No se pudo validar el cambio de turno.');
        // Si ya lo validó o anuló otra persona, se refresca el estado para no quedar trabado.
        await this.cashShiftService.cargarPendiente(this.branchService.workingBranchId());
        return;
      }
      this.resultado.set(resultado);
      await this.cashShiftService.cargarPendiente(this.branchService.workingBranchId());
    } catch (e) {
      console.error('Error al validar el cambio de turno:', e);
      this.uiService.showError('Error inesperado al validar el cambio de turno.');
    } finally {
      this.guardando.set(false);
    }
  }

  /** Cierra la ventana: sin resultado, no se validó nada; con resultado, la caja ya está liberada. */
  cerrar(): void {
    this.dialogRef.close(this.resultado() !== null);
  }
}
